import { DeserializedObject } from '@/src/lib/http/jsonapi/deserializer';
import { AbstractProduct, ConcreteProduct, DeserializedAbstractProduct } from '@/src/lib/product/types';
import { DeserializedConcreteProduct, ExtendedProductListItem } from '@/src/lib/catalog-search/types';
import { DeserializedCartItem } from '@/src/lib/cart/glue/types/deserialized';
import { TrackingEventItem } from '@/src/lib/tracking/types';
import {
  getCurrencyFromAbstractProduct,
  getCurrencyFromConcreteProduct,
  getCurrencyFromDeserializedAbstractProduct,
} from '@/src/lib/product/business/product-prices';

export function isAbstractProduct(
  product:
    | DeserializedCartItem
    | AbstractProduct
    | DeserializedAbstractProduct
    | DeserializedObject<ConcreteProduct>
    | DeserializedConcreteProduct,
): product is AbstractProduct {
  return 'abstractName' in product || ('type' in product && product.type === 'abstract-products');
}
export function isConcreteProduct(
  product:
    | DeserializedCartItem
    | AbstractProduct
    | DeserializedAbstractProduct
    | DeserializedObject<ConcreteProduct>
    | DeserializedConcreteProduct,
): product is DeserializedObject<ConcreteProduct> | DeserializedConcreteProduct {
  return 'type' in product && product.type === 'concrete-products';
}

export function isDeserializedAbstractProductWithConcreteProduct(
  product: DeserializedAbstractProduct | AbstractProduct | TrackingEventItem | DeserializedConcreteProduct,
): product is DeserializedAbstractProduct & {
  concreteProducts: Array<DeserializedObject<DeserializedAbstractProduct>>;
} {
  return (
    'concreteProducts' in product &&
    product.concreteProducts?.length !== undefined &&
    product.concreteProducts?.length > 0
  );
}

export function isDeserializedConcreteProduct(
  product: ConcreteProduct | DeserializedConcreteProduct,
): product is DeserializedConcreteProduct {
  return 'concreteProductPrices' in product;
}

export function isDeserializedAbstractProductWithAbstractProductPrices(
  product: DeserializedAbstractProduct | AbstractProduct | TrackingEventItem | DeserializedConcreteProduct,
): product is DeserializedAbstractProduct & {
  abstractProductPrices: Array<DeserializedObject<DeserializedAbstractProduct>>;
} {
  return (
    'abstractName' in product ||
    ('type' in product && product.type === 'abstract-products' && product.abstractProductPrices?.length > 0)
  );
}

export function isCartItem(
  product: DeserializedCartItem | AbstractProduct | DeserializedAbstractProduct | DeserializedConcreteProduct,
): product is DeserializedCartItem {
  return 'quantity' in product;
}

export function isExtendedProductListItem(
  product:
    | DeserializedCartItem
    | AbstractProduct
    | DeserializedAbstractProduct
    | DeserializedObject<ConcreteProduct>
    | DeserializedConcreteProduct,
): product is ExtendedProductListItem & { extendedAbstractProduct: DeserializedAbstractProduct } {
  return 'extendedAbstractProduct' in product && product.extendedAbstractProduct !== undefined;
}

export function getProductPriceFromItem(
  product:
    | DeserializedAbstractProduct
    | DeserializedConcreteProduct
    | DeserializedObject<DeserializedAbstractProduct>
    | ExtendedProductListItem
    | DeserializedObject<ConcreteProduct>,
): number {
  if (isDeserializedAbstractProductWithConcreteProduct(product) && product.concreteProducts[0].concreteProductPrices) {
    return product.concreteProducts[0].concreteProductPrices[0].price || 0;
  }

  if (isDeserializedAbstractProductWithAbstractProductPrices(product)) {
    return product.abstractProductPrices[0].price || 0;
  }

  if (isAbstractProduct(product)) {
    return product.price;
  }

  if (isConcreteProduct(product)) {
    return product.concreteProductPrices?.[0].price || 0;
  }

  return product.abstractProductPrices?.[0]?.price || 0;
}

export function getCurrencyFromItem(
  product:
    | DeserializedObject<DeserializedAbstractProduct>
    | ExtendedProductListItem
    | DeserializedConcreteProduct
    | DeserializedObject<ConcreteProduct>,
): string {
  if (isAbstractProduct(product) || isExtendedProductListItem(product)) {
    return getCurrencyFromAbstractProduct(product);
  }

  if (isConcreteProduct(product)) {
    return getCurrencyFromConcreteProduct(product);
  }

  return getCurrencyFromDeserializedAbstractProduct(product);
}

export function getConcreteProductSkuFromItem(
  product: DeserializedObject<DeserializedAbstractProduct> | ExtendedProductListItem,
): string {
  if (isDeserializedAbstractProductWithConcreteProduct(product)) {
    return product.concreteProducts[0].sku;
  }

  if (isExtendedProductListItem(product)) {
    return product.extendedAbstractProduct['concrete-products']?.[0].sku;
  }

  return '';
}

export function camelCaseizeConcreteProduct(product: ConcreteProduct): DeserializedConcreteProduct {
  const newProduct = { ...product, type: 'concrete-products', id: product.sku };

  if (product['concrete-product-availabilities']) {
    newProduct.concreteProductAvailabilities = product['concrete-product-availabilities'];
    delete newProduct['concrete-product-availabilities'];
  }

  if (product['concrete-product-prices']) {
    newProduct.concreteProductPrices = product['concrete-product-prices'];
    delete newProduct['concrete-product-prices'];
  }

  return newProduct as DeserializedConcreteProduct;
}
